import {
  Suspense,
  useContext,
  useEffect,
  useCallback,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
} from "react"
import { node, string, oneOf } from "prop-types"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import {
  AlertMessage,
  Loading,
  BreadcrumbNavigationProvider,
  BreadcrumbNavigationPortal,
  TopBannerProvider,
  TopBannerPortal,
} from "source/shared/components"
import { ProtectAgainstChangingSessions } from "source/shared/hooks/useSession"
import WindowDimensionsProvider from "source/shared/contexts/WindowDimensionsProvider"
import { JoltProvider } from "source/shared/contexts/JoltContext"
import { QRCode, QRCodeLink, QRCodeProvider } from "source/shared/QRCode"
import { WebBootContext } from "source/publishing/WebBoot"
import { DraftModeBanner } from "source/publishing/DraftMode"
import { ReactToPublishingJoltEvents } from "source/publishing/ReactToPublishingJoltEvents"
import { ChurchCenterIframeProvider } from "source/shared/contexts/ChurchCenterIframeProvider"
import { TitleTemplate } from "source/TitleTemplate"
import { useLocalStorage } from "source/shared/hooks/useLocalStorage"
import useQueryString from "source/shared/hooks/useQueryString"
import classNames from "classnames"
import { useEmbedded } from "./shared/hooks/useEmbedded"
import { useClientFlag } from "./shared/hooks/useClientFlag"
import OpenGraphTags from "./shared/OpenGraphTags"
import { ChurchCenterIframeCloseButton } from "./shared/contexts/ChurchCenterIframeProvider"
import { LayoutContext } from "./shared/contexts/ThemeContext"
import { useLayoutReducer } from "source/LayoutContext"
import { useRouteLayoutWidth, LAYOUT_WIDTHS } from "source/useRouteLayoutWidth"
import { HydratedHeader } from "source/header/HydratedHeader"
import { useLocation } from "react-router"

const DEFAULT_LAYOUT_WIDTH = LAYOUT_WIDTHS.narrow

Layout.propTypes = {
  children: node.isRequired,
}

function useInReactNativeWebView() {
  return useClientFlag(() => !!window.ReactNativeWebView)
}

export function useIsChurchCenterApp() {
  const { search } = useLocation()
  const inReactNativeWebView = useInReactNativeWebView()

  return search.includes("source=cca") || inReactNativeWebView
}

// Church Center App uses the window to communicate the color scheme to Church Center Web rendered inside
// of web views. The app *MUST* communicate the theme to the web view. We make no inferences.
// The agreed upon contract is `forceTheme`, and `setForceTheme`.
//
// window.forceTheme = ''
// window.setForceTheme('')
//
// Church Center Web, on the other hand, follows the system's prefered color scheme.

window.setForceTheme = (theme) => {
  window.forceTheme = theme
}

export function useColorScheme() {
  const PREFERS_DARK = "(prefers-color-scheme: dark)"
  const features = useContext(WebBootContext).features
  const [queryParams] = useQueryString({
    theme: null,
  })
  const [storedTheme, setStoredTheme] = useLocalStorage(
    "ChurchCenterTheme",
    null,
  )
  let followSystemTheme
  let defaultColorScheme
  const isCCA = useInReactNativeWebView()

  if (isCCA) {
    followSystemTheme = false
    defaultColorScheme = window.forceTheme || "light"
  } else if (features.ROLLOUT_dark_mode && storedTheme) {
    followSystemTheme = false
    defaultColorScheme = storedTheme || "light"
  } else {
    followSystemTheme = false
    defaultColorScheme = "light"
  }

  const [chosenColorScheme, setChosenColorScheme] = useState(defaultColorScheme)
  const overriddenChosenColorScheme = storedTheme || chosenColorScheme

  // Put queryParam-defined theme in local storage
  useEffect(() => {
    if (queryParams.theme !== null) setStoredTheme(queryParams.theme)
  }, [queryParams.theme])

  // Make setForceTheme available to React Native
  useEffect(() => {
    if (!isCCA) return

    window.setForceTheme = setChosenColorScheme
    if (window.forceTheme) setChosenColorScheme(window.forceTheme)
  }, [isCCA, setChosenColorScheme])

  // Actually follow the system theme changing.
  // Not attached while we roll-out dark mode behind a query param.
  useEffect(() => {
    if (!followSystemTheme) return
    const media = window.matchMedia(PREFERS_DARK)
    const handleChange = (event) =>
      setChosenColorScheme(event.matches ? "dark" : "light")
    media.addEventListener("change", handleChange)
    return () => media.removeEventListener("change", handleChange)
  }, [followSystemTheme, setChosenColorScheme])

  const colorScheme = useMemo(() => {
    switch (overriddenChosenColorScheme) {
      case "dark-low":
      case "dark":
        return "dark"
      default:
        return "light"
    }
  }, [overriddenChosenColorScheme])

  // Apply the chosen scheme to the document body.
  useEffect(() => {
    document.body.setAttribute("data-color-scheme", colorScheme)
    document.body.setAttribute("data-contrast", "low")
    document.body.setAttribute("data-background-color", "")
  }, [colorScheme])

  return colorScheme
}

export function Layout({ children }) {
  const { flash } = useContext(ServerRenderedProps)
  const { error } = flash
  const [state, dispatch] = useLayoutReducer()
  const embedded = useEmbedded()
  const theme = useColorScheme()
  const isChurchCenterApp = useIsChurchCenterApp()
  const routeLayoutWidth = useRouteLayoutWidth()
  const layoutWidth =
    state.layoutWidth ?? routeLayoutWidth ?? DEFAULT_LAYOUT_WIDTH

  return (
    <LayoutContext.Provider value={[state, dispatch, theme]}>
      <ChurchCenterIframeProvider>
        <JoltProvider>
          <ReactToPublishingJoltEvents />
          <QRCodeProvider>
            <WindowDimensionsProvider>
              <BreadcrumbNavigationProvider>
                <TopBannerProvider>
                  <OpenGraphTags />
                  <TitleTemplate />
                  <PageContainer layoutWidth={layoutWidth}>
                    <ChurchCenterIframeCloseButton />
                    <TopBannerPortal />
                    <DraftModeBanner />
                    {!embedded && !isChurchCenterApp && <HydratedHeader />}
                    {!isChurchCenterApp && <BreadcrumbNavigationPortal />}

                    <EmbedHeightReportingWrapper
                      id="main_page_content"
                      role="main"
                      className={classNames("f-1", {
                        "page-content": !embedded,
                      })}
                    >
                      <div
                        className={`f-1 container container--${layoutWidth}`}
                      >
                        {error && (
                          <AlertMessage className="mb-3">{error}</AlertMessage>
                        )}
                        {embedded ? (
                          <Suspense fallback={<Loading />}>{children}</Suspense>
                        ) : (
                          <ProtectAgainstChangingSessions>
                            <Suspense fallback={<Loading />}>
                              {children}
                            </Suspense>
                          </ProtectAgainstChangingSessions>
                        )}
                      </div>
                    </EmbedHeightReportingWrapper>
                    {!isChurchCenterApp && <QRCode />}
                    {!embedded && !isChurchCenterApp && <Footer />}
                  </PageContainer>
                </TopBannerProvider>
              </BreadcrumbNavigationProvider>
            </WindowDimensionsProvider>
          </QRCodeProvider>
        </JoltProvider>
      </ChurchCenterIframeProvider>
    </LayoutContext.Provider>
  )
}

function PageContainer({ layoutWidth = "", children }) {
  return (
    <div className={`page-container d-f fd-c ${layoutWidth}`}>{children}</div>
  )
}
PageContainer.propTypes = {
  layoutWidth: string,
  children: node,
}

function Footer() {
  const { layout } = useContext(ServerRenderedProps)
  const {
    organization_contact_email: email,
    organization_contact_phone: phone,
    organization_name: name,
  } = layout

  return (
    <footer className="site-footer" role="contentinfo">
      <ul className="site-footer__links o-2 o-1@sm">
        <li className="site-footer__link">
          <a
            className="c-tint2"
            href="https://planning.center/terms/"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of Service
          </a>
        </li>
        <li className="site-footer__link">
          <a
            className="c-tint2"
            href="https://planning.center/privacy/"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </a>
        </li>
      </ul>

      <ul className="site-footer__links o-1 o-2@sm">
        <li className="site-footer__link">{name}</li>
        {email && (
          <li className="site-footer__link">
            <a className="fw-400 c-tint2" href={`mailto:${email}`}>
              {email}
            </a>
          </li>
        )}

        {phone && (
          <li className="site-footer__link">
            <a className="fw-400 c-tint2" href={`tel:${phone}`}>
              {phone}
            </a>
          </li>
        )}
      </ul>
      <QRCodeLink />
    </footer>
  )
}

function EmbedHeightReportingWrapper({ children, ...props }) {
  const wrapper = useRef(null)
  const embedded = useEmbedded()

  useEffect(() => {
    if (!embedded || !wrapper.current || !window.ResizeObserver) return

    const observer = new ResizeObserver(() => {
      // For gallery view, measure the .container element
      // Otherwise, use document scrollHeight
      const container = wrapper.current.querySelector(".container")
      const contentHeight = container
        ? container.scrollHeight
        : Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
          )
      window.parent.postMessage({ pageHeight: contentHeight }, "*")
    })

    observer.observe(wrapper.current)
    return () => observer.disconnect()
  }, [embedded])

  return (
    <div ref={wrapper} {...props}>
      {children}
    </div>
  )
}
EmbedHeightReportingWrapper.propTypes = {
  children: node,
}

export function useLayoutWidth(width) {
  const [_, dispatch] = useContext(LayoutContext)

  const setLayoutWidth = useCallback(
    (layoutWidth) => {
      dispatch({
        type: "setLayoutWidth",
        payload: layoutWidth,
      })
    },
    [dispatch],
  )

  useLayoutEffect(() => {
    setLayoutWidth(width)
    return () => {
      setLayoutWidth(null)
    }
  }, [width, setLayoutWidth])
}
useLayoutWidth.propTypes = {
  width: oneOf(Object.values(LAYOUT_WIDTHS)).isRequired,
}

export const FullscreenLayout = ({ children }) => {
  useLayoutWidth(LAYOUT_WIDTHS.fullscreen)
  return <>{children}</>
}
FullscreenLayout.propTypes = {
  children: node.isRequired,
}

export const WideLayout = ({ children }) => {
  useLayoutWidth(LAYOUT_WIDTHS.wide)
  return <>{children}</>
}
WideLayout.propTypes = {
  children: node.isRequired,
}

// TODO: Delete this now that it doesn't actually do anything
export const NarrowLayout = ({ children }) => {
  return <>{children}</>
}
NarrowLayout.propTypes = {
  children: node.isRequired,
}
